import {
  Component,
  Host,
  h,
  Element,
  Prop,
  Event,
  EventEmitter,
  Listen,
  Method,
  State,
  Watch,
} from '@stencil/core';
import dayjs from 'dayjs';
import moveSlotChildElementsToLightDom from '../../utils/moveSlotChildElementsToLightDom';

export interface AhaDatepicker extends HTMLAhaDatepickerElement {}

/**
 * A component to make selecting dates easy.
 */
@Component({
  tag: 'aha-datepicker',
  styleUrl: 'aha-datepicker.css',
  shadow: false,
  scoped: true,
})
export class AhaDatepicker {
  @Element() host!: HTMLAhaDatepickerElement;

  private calendarElem!: HTMLAhaCalendarElement;
  private menuContentElem?: HTMLAhaMenuContentElement;

  @State() groupedCalendarDate?: string;

  @Method()
  async setGroupedCalendarDate(value: string) {
    this.groupedCalendarDate = value;
  }

  /** The selected date. Should be of the format `YYYY-MM-DD`. */
  @Prop({ reflect: true }) value: string = '';
  @Watch('value')
  synchronizeGroupedDatepickers() {
    if (!this.group) return;

    const datepickers = document.querySelectorAll(
      `aha-datepicker[group="${this.group}"]`,
    ) as NodeListOf<HTMLAhaDatepickerElement>;

    if (this.value) {
      // Find any other datepickers on the page that belong to the same group. If those other datepickers don't have a value set, then set their calendar's initial year/month to the year/month value of this datepicker.
      datepickers.forEach(el => {
        if (el === this.host) return;
        if (el.value) return;
        el.setGroupedCalendarDate(this.value);
      });
    } else {
      // Find the first datepicker with the same group and a value set and use that as the default year/month for this datepicker's calendar.
      const datepicker = Array.from(datepickers).find(el => el !== this.host && el.value);
      if (datepicker) {
        this.setGroupedCalendarDate(datepicker.value);
      }
    }
  }

  /**
   * Triggers when the value is changed.
   */
  @Event({
    eventName: 'aha-datepicker:change',
    cancelable: true,
    bubbles: true,
    composed: true,
  }) //eslint-disable-line @stencil/decorators-style
  changeEvent!: EventEmitter;

  /**
   * Triggers when the datepicker menu is initially opened but before it is visible.
   */
  @Event({
    eventName: 'aha-datepicker:open',
    cancelable: true,
    bubbles: true,
    composed: true,
  }) //eslint-disable-line @stencil/decorators-style
  openEvent!: EventEmitter;

  /**
   *
   * Triggers when the datepicker menu is visible on the page.
   */
  @Event({
    eventName: 'aha-datepicker:opened',
    cancelable: true,
    bubbles: true,
    composed: true,
  }) //eslint-disable-line @stencil/decorators-style
  openedEvent!: EventEmitter;

  /**
   * Triggers when the datepicker menu is initially closed but before it is hidden.
   */
  @Event({
    eventName: 'aha-datepicker:close',
    cancelable: true,
    bubbles: true,
    composed: true,
  }) //eslint-disable-line @stencil/decorators-style
  closeEvent!: EventEmitter;

  /**
   * Triggers when the datepicker menu is no longer visible on the page.
   */
  @Event({
    eventName: 'aha-datepicker:closed',
    cancelable: true,
    bubbles: true,
    composed: true,
  }) //eslint-disable-line @stencil/decorators-style
  closedEvent!: EventEmitter;

  /**
   * Triggers when the datepicker menu is initially closed but before it is hidden.
   */
  @Event({
    eventName: 'aha-datepicker:cancel',
    cancelable: true,
    bubbles: true,
    composed: true,
  }) //eslint-disable-line @stencil/decorators-style
  cancelEvent!: EventEmitter;

  @Listen('aha-date-input:change')
  handleDateInputChanged(e: CustomEvent) {
    this.value = e.detail.value;
    this.changeEvent.emit(this.host);
  }

  /** The name of the form control that will be submitted via a regular `<form>` submit. */
  @Prop({ reflect: true }) name: string | undefined;

  /** Wether or not the datepicker's calendar view is visible or not. */
  @Prop({ reflect: true }) open: boolean = false;

  /** Placeholder for field when there is no set value. */
  @Prop({ reflect: true }) placeholder: string = '';

  /** Whether or not the `value` of the component can be set, or if it can only be read. */
  @Prop({ reflect: true, attribute: 'readonly' }) readOnly?: boolean;

  /** Whether or not the component is disabled. */
  @Prop({ reflect: true }) disabled?: boolean;

  /** Whether the date inputs will be displayed as [ISO 8061 format](https://www.iso.org/iso-8601-date-and-time-format.html) (YYYY-MM-DD) or according to localized formatting rules (eg. MM/DD/YYYY or DD/MM/YYYY, depending on locale specified in `window.navigator`). */
  @Prop({ reflect: true }) iso?: boolean;

  /** Whether or not a button should be shown next to the datepicker input fields. */
  @Prop({ reflect: true }) button?: boolean;

  /** When the group value is set, then any time this datepicker's value is set, all other aha-datepicker elements with the same group name and with no value set will have their calendar default to same year/month as this datepicker. */
  @Prop({ reflect: true }) group?: string;

  private handleDateInputChange(e: Event) {
    const target = e.target as HTMLElement;
    const dateInput = target.closest('aha-date-input') as HTMLAhaDateInputElement;
    this.value = dateInput.value ? dateInput.value : '';
  }

  private handleKeydown(e: KeyboardEvent) {
    const target = e.target as HTMLElement;
    if (e.key === 'Enter' && target.tagName === 'INPUT') {
      const datepicker = target.closest('aha-datepicker') as HTMLAhaDatepickerElement;
      datepicker.open = !datepicker.open;
    }
  }

  connectedCallback() {
    this.handleDateInputChange = this.handleDateInputChange.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
  }

  componentDidLoad() {
    this.synchronizeGroupedDatepickers();

    // If the embedded menu is being opened, then ensure the datepicker is also opened.
    this.menuContentElem?.addEventListener('aha-menu-content:open', e => {
      const menuContent = e.target as HTMLAhaMenuContentElement;
      this.open = menuContent.open;
      this.openEvent.emit(this.host);
    });

    // If the embedded menu is fully opened, fire off the opened event for the datepicker.
    this.menuContentElem?.addEventListener('aha-menu-content:opened', e => {
      this.openedEvent.emit(this.host);
    });

    // If the embedded menu is being closed, then ensure the datepicker is also closed.
    this.menuContentElem?.addEventListener('aha-menu-content:close', e => {
      const menuContent = e.target as HTMLAhaMenuContentElement;
      this.open = menuContent.open;
      this.closeEvent.emit(this.host);
    });

    // If the embedded menu is closed, then fire the closed event.
    this.menuContentElem?.addEventListener('aha-menu-content:closed', e => {
      this.closedEvent.emit(this.host);
    });

    // If the embedded menu is closed, then fire the closed event.
    this.menuContentElem?.addEventListener('aha-menu-content:cancel', (event: any) => {
      if (event.detail) {
        // @ts-ignore
        const menu = event.detail.closest('aha-menu');
        if (!menu) return;

        // @ts-ignore
        const datepicker = menu.closest('aha-datepicker');
        if (datepicker) {
          const input = datepicker.querySelector(':scope input[type="text"]') as HTMLInputElement;
          input?.focus();
        }
      }
      this.cancelEvent.emit(this.host);
    });

    // Update the datepicker's date when the embedded calendar's date changes.
    this.calendarElem.addEventListener('aha-calendar:selected', (event: any) => {
      if (event.detail) {
        // @ts-ignore
        const menu = event.detail.element.closest('aha-menu');
        if (!menu || !menu.open) return;

        // @ts-ignore
        const datepicker = menu.closest('aha-datepicker');
        if (datepicker) {
          datepicker.value = event.detail.element.date;
          datepicker.open = false;
          const input = datepicker.querySelector(':scope input[type="text"]');
          input?.focus();
        }
      }
    });

    // Close the datepicker menu when a date within the embedded calendar is clicked on.
    this.calendarElem.addEventListener('aha-calendar:date-clicked', (event: any) => {
      if (event.detail) {
        // @ts-ignore
        const menu = event.detail.host.closest('aha-menu');
        if (!menu) return;

        // @ts-ignore
        const datepicker = menu.closest('aha-datepicker');
        if (datepicker) datepicker.open = false;
      }
    });
  }

  render() {
    const viewValue = this.value ? dayjs(this.value).format('YYYY-MM') : this.value;
    const defaultMonth = (
      this.groupedCalendarDate ? dayjs(this.groupedCalendarDate) : dayjs()
    ).format('YYYY-MM');

    return (
      <Host onKeydown={this.handleKeydown}>
        <aha-menu open={this.open} disabled={this.readOnly || this.disabled}>
          <aha-date-input
            slot="control"
            value={this.value}
            name={this.name || ''}
            placeholder={this.placeholder}
            disabled={this.disabled}
            readOnly={this.readOnly}
            onChange={this.handleDateInputChange}
            iso={this.iso}
            button={this.button}
          >
            <slot name="button"></slot>
          </aha-date-input>
          <aha-menu-content
            unrestricted-scroll
            ref={ref => (this.menuContentElem = ref)}
            style={{
              // Add extra padding between the popu and the triggering element to accomodate the
              // triggering elements focus state border
              '--aha-menu-content--vertical-margin': '2px',
              '--aha-menu-content--padding-bottom': '0',
            }}
          >
            {/* @ts-ignore */}
            <aha-calendar
              // @ts-ignore
              ref={ref => (this.calendarElem = ref)}
              date={this.value || dayjs().format('YYYY-MM-DD')}
              start={viewValue || defaultMonth}
              end={viewValue || defaultMonth}
              controls
              uniform
              style={{
                margin: 'var(--spacer-2)',
                paddingLeft: 'var(--spacer-1)',
                paddingRight: 'var(--spacer-1)',
                paddingBottom: 'var(--spacer-1)',
              }} // NOTE: Because the calendar will be lifted out of the datepicker element, the style should be set directly on the element, not in the CSS file.
            ></aha-calendar>
          </aha-menu-content>
        </aha-menu>
      </Host>
    );
  }
}
